import { useMemo, useState } from 'react'
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, formatDuration } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { fetchVenues } from '@/features/venue/venueApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { InteractionFilterBar } from '@/features/crm/CrmFilterBars'
import { CrmRecordSelect } from '@/features/crm/CrmRecordSelect'
import { SubscriberSelect } from '@/features/subscriber/SubscriberSelect'
import {
  createInteraction,
  fetchInteractionPage,
  updateInteraction,
  voidInteraction,
  INTERACTION_CHANNELS,
  INTERACTION_DIRECTIONS,
  INTERACTION_OUTCOMES,
  type Interaction,
  type InteractionChannel,
  type InteractionDirection,
  type InteractionEdit,
  type InteractionOutcome,
} from '@/features/crm/crmApi'

/** Bog'lanish turi — FAQAT forma ichida yashaydi, serverga ketmaydi */
type LinkKind = 'VENUE' | 'TICKET' | 'LEAD' | 'SUBSCRIBER'

interface InteractionFormValues {
  linkKind: LinkKind
  venueId?: string
  ticketId?: string
  leadId?: string
  subscriberId?: string
  channel: InteractionChannel
  direction: InteractionDirection
  subject?: string
  notes?: string
  outcome?: InteractionOutcome
  durationSeconds?: number
}

const OUTCOME_COLOR: Record<string, string> = {
  RESOLVED: 'green',
  FOLLOW_UP_NEEDED: 'gold',
  NO_ANSWER: 'default',
  ESCALATED: 'red',
  OTHER: 'default',
}

/**
 * Muloqotlar — mijoz bilan har bir aloqa yozuvi.
 *
 * <h2>⚠️ Bu jurnal — 2026-08-15 da qaror O'ZGARDI</h2>
 * Ilgari bu yerda «tahrirlash tugmasi qidirmang: u yo'qligi nuqson
 * emas» deb yozilgan edi. Tashqi tester (CRUD-CRM-INTERACTION) buni
 * nuqson deb ko'rsatdi va egasi qarorni o'zgartirdi.
 *
 * <p>Endi ikki amal bor, lekin jurnal xossasi SAQLANDI:
 * <ul>
 *   <li><b>Tuzatish</b> — faqat operatorning O'Z bayoni (mavzu, izoh,
 *       natija, davomiylik). Hodisaning o'zi — vaqt, vosita,
 *       yo'nalish, bog'lanish — tegilmaydi, ya'ni "boshqa hodisa
 *       yozish" mumkin emas. Tuzatilgan yozuv «tahrirlangan» belgisi
 *       bilan ochiq ko'rsatiladi.</li>
 *   <li><b>"O'chirish"</b> — aslida YUMSHOQ bekor. Qator bazada
 *       qoladi, faqat ro'yxatdan chiqadi, kim va nega bekor qilgani
 *       yoziladi. Xom {@code DELETE} qo'shilMADI: begona tiketga
 *       qo'yilgan soxta yozuvni izsiz yo'q qilish yo'li ochilmasin
 *       (sabab — {@code InteractionResource#voidEntry}).</li>
 * </ul>
 *
 * <h2>⚠️ Davomiylik ixtiyoriy va u SHUNDAY qoladi</h2>
 * Qo'ng'iroq necha soniya davom etganini xodim har doim ham
 * bilmaydi. Majburiy qilish uni "0" yozishga undardi va statistika
 * yolg'on bo'lardi.
 */
export function InteractionsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const { can } = usePermissions()
  const { user } = useAuth()
  const defaultLinkKind: LinkKind = user?.tenant.businessKind === 'VENUE' ? 'VENUE' : 'SUBSCRIBER'

  /*
    Bo'sh holat IKKI XIL: "yozuv yo'q" va "filtr hech narsa topmadi".
    Manba bitta — URL holati, ya'ni `DataTable` bilan zid bo'la olmaydi.
  */
  const { state: tableState, reset: resetTable } = useTableUrlState()
  const isFiltered = Object.keys(tableState.filters).length > 0

  /**
   * ⚠️ NOMSIZ OB'EKT HAVOLASI — B3 nuqsonining ikkinchi yarmi.
   *
   * `crm.interaction.venue_name` — DENORMALLASHTIRILGAN KESH va u
   * FAQAT tiketdan meros olinganda to'ladi
   * (`InteractionServiceImpl#log`). Operator ob'ektni forma orqali
   * TO'G'RIDAN-TO'G'RI tanlaganda — ya'ni sukut bo'yicha yo'lda,
   * `linkKind = VENUE` — qator `venue_id` bilan, `venue_name` siz
   * yoziladi.
   *
   * ⚠️ Nomni backend to'ldirmaydi va bu ATAYLAB: `xsib-crm` `venue`
   * sxemasiga murojaat qilmaydi (`docs/10-MIKROSERVIS.md` §1, §7 —
   * kesishgan o'qish faqat `xsib-report` da). Bitta yorliq uchun
   * `xsib-venue` ga yangi servislararo klient qo'shish — chegarani
   * buzish, `LeadWonModal` dagi bilan bir xil qaror.
   *
   * Shuning uchun nom PANELDA hal qilinadi: ro'yxat allaqachon
   * ochiq API va u shu sahifada modal uchun ham tortiladi.
   */
  /*
    Ob'ektlar ro'yxati — mavzu tanlash VA jadvaldagi nomlar uchun.

    ⚠️ Shart ilgari `createOpen || needsVenueNames` edi va
    `needsVenueNames` JADVAL QATORLARIDAN hisoblanardi. Jadval endi
    `DataTable` ichida va qatorlar sahifada YO'Q — o'sha shartni
    saqlash uchun ro'yxatni ikkinchi marta tortish kerak bo'lardi.

    Shuning uchun shart SODDALASHTIRILDI, lekin va'da SAQLANDI: ro'yxat
    modal yopiq turganda ham so'raladi, ya'ni jadval nomni ko'rsata
    oladi. Qo'shimcha so'rov ham yo'q — `InteractionFilterBar` AYNAN
    shu kalit bilan so'raydi va TanStack Query ikkalasini bitta
    so'rovga birlashtiradi.

    ⚠️ `venue.read` YO'Q rolda so'rov UMUMAN yuborilmaydi: usiz u har
    sahifa ochilishida 403 berardi, ustun esa baribir «ob'ektga
    bog'langan» yorlig'iga tushardi.
  */
  const canReadVenues = can('venue.read')
  const venues = useQuery({
    queryKey: queryKeys.venues.list(null),
    queryFn: () => fetchVenues(null),
    enabled: canReadVenues,
  })

  const venueNameById = new Map((venues.data ?? []).map((v) => [v.id, v.name]))
  /**
   * ⚠️ `linkKind` — FAQAT FORMANING o'zi uchun: u qaysi bog'lanish
   * maydoni ko'rsatilishini boshqaradi. Serverga YUBORILMAYDI
   * (`InteractionDraft` da bunday maydon yo'q). Ilgari forma
   * qiymatlari to'g'ridan-to'g'ri `createInteraction` ga berilardi va
   * `linkKind` so'rov tanasiga tushib ketardi: bugun u jimgina
   * e'tiborsiz qoldiriladi, lekin `FAIL_ON_UNKNOWN_PROPERTIES` bir kun
   * yoqilsa BUTUN forma 400 bilan yiqilardi — va sababi tanada
   * ko'rinmasdi.
   */
  const [form] = Form.useForm<InteractionFormValues>()

  const create = useMutation({
    // ⚠️ `linkKind` bu yerda TASHLAB KETILADI — u formaning ichki
    // maydoni (yuqoridagi izoh). Qolgani kontraktga bir-bir mos.
    mutationFn: ({ linkKind: _linkKind, ...draft }: InteractionFormValues) =>
      createInteraction(draft),
    onSuccess: () => {
      setCreateOpen(false)
      form.resetFields()
      void queryClient.invalidateQueries({ queryKey: ['crm', 'interactions'] })
    },
    // ⚠️ `onError` ATAYLAB yo'q: bu mutatsiya OYNAGA bog'langan
    // (`confirmLoading={create.isPending}`) va xatosi sahifa banneriga
    // chiqsa, modal niqobi ortida qolardi.
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const closeCreate = () => {
    create.reset()
    setCreateOpen(false)
  }

  // ===================================================================
  // TUZATISH va BEKOR QILISH — 2026-08-15 da qo'shildi
  //
  // ⚠️ Tester (CRUD-CRM-INTERACTION) "tahrirlash ham, o'chirish ham
  //    yo'q" deb yozdi. Ilgari bu ATAYLAB edi (sinf javadoc'i), egasi
  //    qarorni o'zgartirdi.
  //
  // ⚠️ "O'chirish" tugmasi aslida YUMSHOQ bekor qiladi: qator bazada
  //    qoladi, faqat ro'yxatdan chiqadi. Operator uchun farqi yo'q,
  //    tergov uchun MUHIM — sabab `InteractionResource#voidEntry` da.
  // ===================================================================
  const [editing, setEditing] = useState<Interaction | null>(null)
  const [voiding, setVoiding] = useState<Interaction | null>(null)
  const [editForm] = Form.useForm<InteractionEdit>()
  const [voidForm] = Form.useForm<{ reason: string }>()

  const edit = useMutation({
    mutationFn: (values: InteractionEdit) => updateInteraction(editing!.id, values),
    onSuccess: () => {
      setEditing(null)
      editForm.resetFields()
      void queryClient.invalidateQueries({ queryKey: ['crm', 'interactions'] })
    },
  })

  const voidEntry = useMutation({
    mutationFn: ({ reason }: { reason: string }) => voidInteraction(voiding!.id, reason),
    onSuccess: () => {
      setVoiding(null)
      voidForm.resetFields()
      void queryClient.invalidateQueries({ queryKey: ['crm', 'interactions'] })
    },
  })

  /**
   * ⚠️ Forma MAVJUD qiymatlar bilan to'ldirib ochiladi.
   *
   * Server tomoni PUT semantikasida: yuborilmagan maydon `null`
   * bo'ladi. Agar forma bo'sh ochilsa, operator faqat mavzuni
   * tuzatib saqlaganda IZOH JIMGINA o'chib ketardi.
   */
  const openEdit = (row: Interaction) => {
    edit.reset()
    setEditing(row)
    editForm.setFieldsValue({
      subject: row.subject ?? undefined,
      notes: row.notes ?? undefined,
      outcome: (row.outcome as InteractionOutcome | undefined) ?? undefined,
      durationSeconds: row.durationSeconds ?? undefined,
    })
  }

  const openVoid = (row: Interaction) => {
    voidEntry.reset()
    voidForm.resetFields()
    setVoiding(row)
  }

  /*
    ⚠️ Tartib raqami ustuni QO'LDA yozilmaydi — `DataTable` uni o'zi
    qo'shadi va sahifa siljishini hisobga oladi. Server sahifasi
    NOLDAN, jadval BIRDAN sanaydi va bu farq endi bitta joyda —
    `fetchInteractionPage` da — tarjima qilinadi.
  */
  const columns = useMemo<ColumnsType<Interaction>>(() => [
    {
      key: 'occurredAt',
      title: t('crm.interaction.occurredAt'),
      width: 170,
      render: (_, row) => formatDateTime(row.occurredAt),
    },
    {
      key: 'channel',
      title: t('crm.interaction.channel'),
      width: 130,
      render: (_, row) => (
        <Space size={4}>
          <Tag>{t(`crm.channel.${row.channel}`)}</Tag>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t(`crm.direction.${row.direction}`)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'who',
      title: t('crm.interaction.who'),
      width: 200,
      /*
        ⚠️ Muloqot TO'RT narsaga bog'lanishi mumkin: tiket, lid,
        abonent yoki OB'EKT. Eski yozuvlar hech biriga bog'lanmagan
        bo'lishi mumkin (yangi qo'shishga endi yo'l qo'yilmaydi) —
        bo'sh qolgani o'sha holat uchun xato emas.

        ⚠️ B3 NUQSONI AYNAN SHU YERDA EDI: zanjir `venueName` ga
        qarardi, `venueId` ga esa YO'Q. Ob'ekt forma orqali
        to'g'ridan-to'g'ri tanlanganda nom bo'sh keladi (sabab
        `needsVenueNames` izohida), ya'ni SAQLANGAN bog'lanishi BOR
        yozuv «Bog'lanmagan» bo'lib ko'rinardi — va bu sukut bo'yicha
        yo'l edi.

        Endi uch pog'ona: kesh nomi → paneldagi ro'yxatdan nom →
        nom umuman topilmasa ham «ob'ektga bog'langan» degan HALOL
        yorliq. Uchinchisi zarur: ob'ekt o'chirilgan yoki doiradan
        tashqarida bo'lsa nomi topilmaydi, lekin bog'lanish BOR va
        uni «yo'q» deb ko'rsatish yana o'sha yolg'on bo'lardi.
      */
      render: (_, row) => {
        const venue = row.venueName ?? (row.venueId ? venueNameById.get(row.venueId) : undefined)
        return (
          row.subscriberLogin ?? venue
          ?? (row.venueId ? t('crm.interaction.onVenue') : null)
          ?? (row.ticketId ? t('crm.interaction.onTicket') : null)
          ?? (row.leadId ? t('crm.interaction.onLead') : null)
          ?? <Typography.Text type="secondary">{t('crm.interaction.standalone')}</Typography.Text>
        )
      },
    },
    {
      key: 'subject',
      title: t('crm.interaction.subject'),
      width: 260,
      // ⚠️ Tuzatilgan yozuv OCHIQ belgilanadi. Tuzatishni yashirish
      //    jurnalga ishonchni yo'qotardi: o'quvchi matn boshidanoq
      //    shundaymi yoki keyin o'zgartirilganmi — bilishi kerak.
      render: (_, row) => (
        <Space size={4}>
          <span>{row.subject ?? '—'}</span>
          {row.editedAt ? (
            <Tag color="default" title={formatDateTime(row.editedAt)}>
              {t('crm.interaction.edited')}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'outcome',
      title: t('crm.interaction.outcome'),
      width: 170,
      render: (_, row) =>
        row.outcome ? (
          <Tag color={OUTCOME_COLOR[row.outcome]}>{t(`crm.outcome.${row.outcome}`)}</Tag>
        ) : (
          '—'
        ),
    },
    {
      key: 'duration',
      title: t('crm.interaction.duration'),
      width: 120,
      render: (_, row) => (row.durationSeconds ? formatDuration(row.durationSeconds) : '—'),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      // ⚠️ Har amal O'Z huquqi ostida. Ikkisi ATAYLAB ajratilgan:
      //    imlo xatosini tuzatish ruxsati o'z izini yashirish
      //    ruxsatini bermasin (`Permission.CRM_INTERACTION_VOID`).
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              onClick: () => openEdit(row),
              permission: 'crm.interaction.update',
            },
            {
              key: 'void',
              label: t('actions.delete'),
              onClick: () => openVoid(row),
              permission: 'crm.interaction.void',
              danger: true,
              divider: true,
            },
          ]}
        />
      ),
    },
    // ⚠️ `openEdit`/`openVoid` bog'liqliklarda ATAYLAB yo'q: ular har
    //    chizishda qayta yaratiladigan funksiyalar va ro'yxatga
    //    qo'shilsa `useMemo` umuman ishlamay qolardi. Ular faqat
    //    holat o'rnatuvchilarni chaqiradi, ya'ni eskirmaydi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, venueNameById])

  return (
    <>
      <PageHeader
        title={t('crm.interaction.title')}
        description={t('crm.interaction.subtitle')}
        extra={
          <Can permission="crm.interaction.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('crm.interaction.create')}
            </Button>
          </Can>
        }
      />

      {/*
        ⚠️ RO'YXAT XATOSI KO'RINADI — endi uni `DataTable` chizadi:
        sababi (`translateError`) va «qayta urinish» tugmasi bilan.
        Ilgari bu yerda alohida banner turardi; ikkitasi bir xil xatoni
        ikki marta ko'rsatardi. Muhimi o'zgarmadi: "yozuv yo'q" bilan
        "olib bo'lmadi" hamon AJRATILGAN.

        ⚠️ SAHIFALASH SERVERDA (`fetchInteractionPage`) —
        `InteractionQuery` `page`/`size` ni haqiqatdan qabul qiladi.

        ⚠️ `searchable` YO'Q va bu ataylab: `InteractionQuery` da matn
        qidiruvi umuman yo'q. Maydonni chizib, so'rovga hech narsa
        qo'shmaslik «qidiruv buzuq» degan xulosaga olib borardi.

        ⚠️ YIG'INDI: 170+130+200+260+170+120+56 = 1106. Tartib raqami
        (56) `DataTable` tomonidan qo'shiladi va `scrollX` uni O'ZI
        hisobga oladi (`minTableWidth`).
      */}
      <DataTable<Interaction>
        queryKey={queryKeys.crm.interactionList}
        fetchPage={fetchInteractionPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="crm-interactions"
        stickyFirstColumn
        toolbarExtra={<InteractionFilterBar />}
        scrollX={1106}
        {...(isFiltered
          ? {
              emptyTitle: t('dataTable.emptyFiltered'),
              emptyDescription: t('dataTable.emptyFilteredHint'),
              emptyAction: { label: t('dataTable.resetFilters'), onClick: resetTable },
            }
          : {
              emptyTitle: t('crm.interaction.emptyTitle'),
              emptyDescription: t('crm.interaction.emptyHint'),
              ...(can('crm.interaction.create')
                ? {
                    emptyAction: {
                      label: t('crm.interaction.create'),
                      onClick: () => setCreateOpen(true),
                    },
                  }
                : {}),
            })}
      />

      <Modal
        open={createOpen}
        title={t('crm.interaction.create')}
        onCancel={closeCreate}
        onOk={() => submitOnValid(form, (values) => create.mutate(values))}
        confirmLoading={create.isPending}
        okText={t('operation.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          {/*
            ⚠️ BOG'LANISH MAJBURIY — backend qoidasi.

            "Aloqa yozuvi kamida bitta mavzuga bog'liq bo'lishi kerak:
            tiket, lid, abonent yoki ob'ekt". Bu maydon formada YO'Q edi
            va operator "Yaratish" bosganda xom 400 olardi.

            ⚠️ SABABI MA'NOLI: bog'lanmagan yozuv jurnalda "kimdir
            qo'ng'iroq qildi" bo'lib qolardi va uni keyin hech qaysi
            mijoz tarixidan topib bo'lmasdi — ya'ni yozuvning qiymati
            nolga teng.

            Ro'yxatda esa "Bog'lanmagan" ustuni bor: eski yozuvlar
            ko'rinishi kerak, yangi qo'shishga esa yo'l qo'yilmaydi.
          */}
          <Form.Item
            name="linkKind"
            label={t('crm.interaction.linkTo')}
            initialValue={defaultLinkKind}
            extra={t('crm.interaction.linkHint')}
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'VENUE', label: t('crm.interaction.linkVenue') },
                { value: 'TICKET', label: t('crm.interaction.linkTicket') },
                { value: 'LEAD', label: t('crm.interaction.linkLead') },
                { value: 'SUBSCRIBER', label: t('crm.interaction.linkSubscriber') },
              ]}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev: { linkKind?: string }, next: { linkKind?: string }) =>
              prev.linkKind !== next.linkKind
            }
          >
            {({ getFieldValue }) => {
              const kind = (getFieldValue('linkKind') ?? defaultLinkKind) as LinkKind
              if (kind === 'VENUE') {
                return (
                  <Form.Item
                    name="venueId"
                    label={t('crm.interaction.linkVenue')}
                    rules={[{ required: true, message: t('crm.interaction.linkRequired') }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      loading={venues.isPending}
                      options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
                    />
                  </Form.Item>
                )
              }
              if (kind === 'SUBSCRIBER') {
                return (
                  <Form.Item
                    name="subscriberId"
                    label={t('crm.interaction.linkSubscriber')}
                    rules={[{ required: true, message: t('crm.interaction.linkRequired') }]}
                  >
                    <SubscriberSelect />
                  </Form.Item>
                )
              }
              return (
                <Form.Item
                  name={kind === 'TICKET' ? 'ticketId' : 'leadId'}
                  label={t(kind === 'TICKET' ? 'crm.interaction.linkTicket' : 'crm.interaction.linkLead')}
                  rules={[{ required: true, message: t('crm.interaction.linkRequired') }]}
                >
                  <CrmRecordSelect kind={kind === 'TICKET' ? 'ticket' : 'lead'} />
                </Form.Item>
              )
            }}
          </Form.Item>

          <Form.Item
            name="channel"
            label={t('crm.interaction.channel')}
            initialValue="CALL"
            rules={[{ required: true }]}
          >
            <Select
              options={INTERACTION_CHANNELS.map((c) => ({ value: c, label: t(`crm.channel.${c}`) }))}
            />
          </Form.Item>
          <Form.Item
            name="direction"
            label={t('crm.interaction.direction')}
            initialValue="INBOUND"
            rules={[{ required: true }]}
          >
            <Select
              options={INTERACTION_DIRECTIONS.map((d) => ({
                value: d,
                label: t(`crm.direction.${d}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="subject" label={t('crm.interaction.subject')}>
            <Input />
          </Form.Item>
          <Form.Item name="outcome" label={t('crm.interaction.outcome')}>
            <Select
              allowClear
              options={INTERACTION_OUTCOMES.map((o) => ({ value: o, label: t(`crm.outcome.${o}`) }))}
            />
          </Form.Item>
          <Form.Item
            name="durationSeconds"
            label={t('crm.interaction.duration')}
            extra={t('crm.interaction.durationHint')}
          >
            <InputNumber min={0} style={{ width: '100%' }} addonAfter={t('units.second')} />
          </Form.Item>
          <Form.Item name="notes" label={t('crm.interaction.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/*
        TUZATISH oynasi.

        ⚠️ Bu yerda faqat operatorning O'Z bayoni bor. Vaqt, vosita,
        yo'nalish va bog'lanish ATAYLAB yo'q — ular hodisaning O'ZI va
        server ham ularni qabul qilmaydi (`InteractionEdit`). Ya'ni
        forma serverdagi shartnomaning aynan aksi: yuborib bo'lmaydigan
        maydon umuman ko'rinmaydi.
      */}
      <Modal
        open={editing !== null}
        title={t('crm.interaction.edit')}
        onCancel={() => {
          edit.reset()
          setEditing(null)
        }}
        onOk={() => submitOnValid(editForm, (values) => edit.mutate(values))}
        confirmLoading={edit.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {edit.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(edit.error, t)}
          />
        ) : null}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('crm.interaction.editHint')}
        />
        <Form form={editForm} layout="vertical">
          <Form.Item name="subject" label={t('crm.interaction.subject')}>
            <Input />
          </Form.Item>
          <Form.Item name="outcome" label={t('crm.interaction.outcome')}>
            <Select
              allowClear
              options={INTERACTION_OUTCOMES.map((value) => ({
                value,
                label: t(`crm.outcome.${value}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="durationSeconds" label={t('crm.interaction.duration')}>
            <InputNumber min={0} style={{ width: '100%' }} addonAfter={t('units.second')} />
          </Form.Item>
          <Form.Item name="notes" label={t('crm.interaction.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/*
        BEKOR QILISH oynasi.

        ⚠️ Sabab MAJBURIY va bu server tomonida ham majburlanadi
        (`InteractionServiceImpl#voidEntry`) — bu yerdagi qoida faqat
        birinchi to'siq. "Shunchaki bosdim" bilan soxta yozuvni
        ko'rinmas qilib bo'lmasin.

        ⚠️ Matn OCHIQ aytadi: yozuv o'chmaydi. Operatorga "yo'q qildim"
        degan yolg'on tasavvur bermaslik kerak — u audit jurnalida
        baribir ko'rinadi.
      */}
      <Modal
        open={voiding !== null}
        title={t('crm.interaction.void')}
        onCancel={() => {
          voidEntry.reset()
          setVoiding(null)
        }}
        onOk={() => submitOnValid(voidForm, (values) => voidEntry.mutate(values))}
        confirmLoading={voidEntry.isPending}
        okText={t('crm.interaction.voidConfirm')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {voidEntry.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(voidEntry.error, t)}
          />
        ) : null}
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('crm.interaction.voidHint')}
        />
        <Typography.Paragraph type="secondary">
          {voiding?.subject ?? '—'} · {voiding ? formatDateTime(voiding.occurredAt) : ''}
        </Typography.Paragraph>
        <Form form={voidForm} layout="vertical">
          <Form.Item
            name="reason"
            label={t('crm.interaction.voidReason')}
            rules={[{ required: true, message: t('crm.interaction.voidReasonRequired') }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
